/**
 * ServicesHubPage — /sluzby index page listing EVERY TopRankPulse service.
 *
 * Design intent:
 *   - Dark theme hero + 4 grouped sections (Audits · Subscriptions · Add-ons · One-time deals)
 *   - Each service card has:
 *       - name + price + 1-line summary + 3-bullet value prop
 *       - 3 CTAs: "Viac info" (details page) + "Kúpiť teraz" (Stripe) + a "Pridať k scanu" link to /snapshot
 *   - Big final OrderNowBottomCTA pointing to the flagship Pro plan.
 *
 * Prices mirror backend/routes/payments.py PACKAGES (source of truth).
 * The /api/payments/checkout endpoint routes subscription packages through
 * the native Stripe subscription mode automatically (billing.py).
 */
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  Zap, Crown, Rocket, Shield, Brain, BarChart3, Eye, Users, Sparkles,
  FileText, Package, Target, Clock, Infinity, Flame, TrendingUp, Search,
  Megaphone, ArrowRight, CheckCircle2,
} from "lucide-react";
import { HeroBuyButton, InlineBuyLink, OrderNowBottomCTA, TrustBarSmall } from "../components/common/ServiceOrderBundle";

// Service catalog — mirrors PACKAGES in payments.py
const SERVICES = {
  flagship: [
    {
      id: "pro_monthly",
      icon: Crown,
      name: "TopRankPulse Pro",
      price: 49,
      interval: "mes",
      summary: "Neobmedzený AI-SEO monitoring pre firmy, ktoré chcú rásť v AI vyhľadávačoch.",
      bullets: [
        "Neobmedzené AI audity (ChatGPT, Perplexity, Gemini, Claude, Copilot, Google AI)",
        "Denné citácie monitoring + týždenný email report",
        "Programmatic SEO landing pages pre tvoju firmu (ORSF/ARES)",
        "Email weekly reports + API access",
      ],
      detailsHref: "/pricing",
      recommended: true,
    },
    {
      id: "business_monthly",
      icon: Rocket,
      name: "Business",
      price: 169,
      interval: "mes",
      summary: "Pre marketingové tímy a stredné firmy s viacerými domémami.",
      bullets: [
        "Všetko z Pro + viac domén",
        "Priority support + onboarding call",
        "Konkurenčný tracking (až 10 domén)",
        "Custom brand monitoring queries",
      ],
      detailsHref: "/pricing",
    },
    {
      id: "enterprise_monthly",
      icon: Shield,
      name: "Enterprise",
      price: 399,
      interval: "mes",
      summary: "SLA, dedikovaný account manager, custom integrácie.",
      bullets: [
        "Všetko z Business + unlimited",
        "SLA + 24/7 support",
        "White-label export reports",
        "Custom AI models + enterprise security",
      ],
      detailsHref: "/pricing",
    },
  ],
  addons: [
    {
      id: "addon_security_monitor",
      icon: Shield,
      name: "Sledovanie bezpečnosti webu",
      price: 19,
      interval: "mes",
      summary: "Týždenná kontrola bezpečnosti a dôveryhodnosti webu s upozornením emailom.",
      bullets: [
        "HTTPS certifikát, bezpečnostné hlavičky, zmiešaný obsah",
        "Ochrana emailu pred podvrhnutím (SPF, DMARC)",
        "Až 5 webov, email pri každom novom probléme",
      ],
      detailsHref: "/bezpecnost-webu",
    },
    {
      id: "addon_attribution",
      icon: BarChart3,
      name: "AI Attribution",
      price: 39,
      interval: "mes",
      summary: "Ktorá AI ti prináša reálny traffic a konverzie — reálne čísla, nie odhady.",
      bullets: [
        "Trackuje traffic z ChatGPT, Perplexity, Gemini, Copilot",
        "Per-engine ROI odhad v €",
        "Session attribution + konverzný mapping",
      ],
      detailsHref: "/attribution",
    },
    {
      id: "addon_ai_intelligence",
      icon: Brain,
      name: "AI Intelligence Bundle",
      price: 39,
      interval: "mes",
      summary: "Brand monitoring + citation tracking + sentiment + competitor gaps.",
      bullets: [
        "Prompt coverage: čo AI o tebe hovorí",
        "Citation sources: koho AI cituje",
        "Sentiment analýza + competitor gaps",
      ],
      detailsHref: "/ai-intelligence",
    },
    {
      id: "addon_brand_monitor",
      icon: Eye,
      name: "Brand Monitor",
      price: 19,
      interval: "mes",
      summary: "Sleduj spomínania tvojej značky naprieč webom a AI modelmi.",
      bullets: [
        "Alerty keď o tebe píšu novinári",
        "AI engine mention tracking",
        "Weekly brand pulse report",
      ],
      detailsHref: "/brand-monitor",
    },
    {
      id: "addon_competitor_spy",
      icon: Users,
      name: "Competitor Spy",
      price: 29,
      interval: "mes",
      summary: "Monitoruj až 5 konkurentov — ich SEO, AI citácie a content changes.",
      bullets: [
        "Daily competitor scan",
        "AI citation delta tracking",
        "Content change alerts",
      ],
      detailsHref: "/competitor-spy",
    },
    {
      id: "addon_autopilot",
      icon: Rocket,
      name: "SEO Autopilot",
      price: 79,
      interval: "mes",
      summary: "Plne automatizovaný SEO + GEO (Generative Engine Optimization) agent.",
      bullets: [
        "Automatické fix suggestions každý týždeň",
        "GEO optimization pre AI vyhľadávače",
        "Monthly priority report",
      ],
      detailsHref: "/seo-autopilot",
    },
    {
      id: "addon_ads_creator",
      icon: Megaphone,
      name: "Ads Creator (30 ads/mo)",
      price: 69,
      interval: "mes",
      summary: "AI-generované reklamné kreatíva pre FB/Instagram/Google Ads.",
      bullets: [
        "30 reklamných variantov mesačne",
        "Multi-format: static, carousel, video",
        "A/B test ready export",
      ],
      detailsHref: "/ads-creator",
    },
  ],
  seo_command: [
    {
      id: "seo_command_starter",
      icon: Target,
      name: "SEO Command · Starter",
      price: 49,
      interval: "mes",
      summary: "GSC OAuth + Content Briefs + Rank Tracking pre jednu doménu.",
      bullets: [
        "Google Search Console OAuth integration",
        "Content Brief generator",
        "Rank tracking 500 keywords",
      ],
      detailsHref: "/seo-command",
    },
    {
      id: "seo_command_growth",
      icon: TrendingUp,
      name: "SEO Command · Growth",
      price: 149,
      interval: "mes",
      summary: "Všetko zo Starter + Local SEO + Review Automator + 5 domén.",
      bullets: [
        "5 domén",
        "Google Business Profile OAuth",
        "Review Automator + Citation Checklist",
      ],
      detailsHref: "/seo-command",
      recommended: true,
    },
    {
      id: "seo_command_agency",
      icon: Flame,
      name: "SEO Command · Agency",
      price: 399,
      interval: "mes",
      summary: "White-label pre agentúry, unlimited domén, WordPress plugin.",
      bullets: [
        "Unlimited domén + white-label",
        "WordPress plugin + Agency API",
        "Priority onboarding",
      ],
      detailsHref: "/seo-command",
    },
  ],
  onetime: [
    {
      id: "snapshot_tripwire",
      icon: Zap,
      name: "AI Visibility Snapshot",
      price: 7,
      interval: null,
      summary: "10-minútový self-serve audit tvojej AI viditeľnosti. Jednorazový.",
      bullets: [
        "Reálne odhady ChatGPT, Perplexity, Gemini citácií",
        "PDF export + email report",
        "14-dňová záruka vrátenia peňazí",
      ],
      detailsHref: "/snapshot",
    },
    {
      id: "benchmark_report",
      icon: FileText,
      name: "Benchmark Report",
      price: 14,
      interval: null,
      summary: "Porovnanie voči 3 konkurentom. Pre-order PDF report.",
      bullets: [
        "3-konkurent AI citation benchmark",
        "SWOT analýza + priority fix list",
        "48h delivery",
      ],
      detailsHref: "/benchmark",
    },
    {
      id: "revenue_leak_audit",
      icon: Search,
      name: "Revenue Leak Audit + 5-day Plan",
      price: 47,
      interval: null,
      summary: "Komplet audit + 5-dňový fix-plán. Pre firmy ktoré strácajú traffic.",
      bullets: [
        "Komplet revenue leak scan",
        "5-day action plan",
        "30-day email check-in",
      ],
      detailsHref: "/revenue-leak",
    },
    {
      id: "lifetime_deal",
      icon: Infinity,
      name: "TopRankPulse Lifetime",
      price: 297,
      interval: null,
      summary: "Dožitie prístup k Pro — 1 jednorazová platba namiesto €49/mes navždy.",
      bullets: [
        "Platíš raz, používaš navždy",
        "Všetky Pro funkcie + budúce updates",
        "Limitované na 100 miest",
      ],
      detailsHref: "/lifetime",
      recommended: true,
    },
    {
      id: "blog_pack_25",
      icon: Package,
      name: "Blog Pack Starter (25 článkov)",
      price: 25,
      interval: null,
      summary: "25 SEO-optimalizovaných blog článkov generovaných Claude Sonnet 4.5.",
      bullets: [
        "25 článkov × 700+ slov",
        "SEO schema markup + OG images",
        "Export v WordPress-ready formáte",
      ],
      detailsHref: "/blog-packs",
    },
  ],
};

function ServiceCard({ s }) {
  const Icon = s.icon;
  const priceLabel = s.interval ? `€${s.price}/${s.interval}` : `€${s.price} jednorazový`;
  return (
    <div
      data-testid={`service-card-${s.id}`}
      className={`relative group flex flex-col rounded-2xl border p-6 sm:p-7 transition-all hover:-translate-y-1 ${
        s.recommended
          ? "border-[#a855f7]/60 bg-gradient-to-br from-[#a855f7]/10 to-[#1a1225]"
          : "border-white/10 bg-white/5 hover:border-white/20"
      }`}
    >
      {s.recommended && (
        <span
          data-testid={`service-card-${s.id}-badge`}
          className="absolute -top-3 left-6 px-3 py-1 bg-[#a855f7] text-white text-[10px] font-black uppercase tracking-widest rounded-full"
        >
          Odporúčané
        </span>
      )}
      <div className="flex items-center gap-3 mb-4">
        <div className="shrink-0 w-11 h-11 rounded-xl bg-[#a855f7]/15 flex items-center justify-center">
          <Icon className="w-5 h-5 text-[#c084fc]" />
        </div>
        <div className="min-w-0">
          <h3 className="text-lg sm:text-xl font-black font-['Cabinet_Grotesk'] text-white truncate">{s.name}</h3>
          <p className="text-xs text-white/60 font-['JetBrains_Mono'] uppercase tracking-wider">{priceLabel}</p>
        </div>
      </div>

      <p className="text-sm text-white/80 leading-relaxed mb-4">{s.summary}</p>

      <ul className="space-y-1.5 text-sm text-white/75 mb-6 flex-1">
        {s.bullets.map((b) => (
          <li key={b} className="flex gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="flex flex-col gap-2 mt-auto">
        <InlineBuyLink
          packageId={s.id}
          priceEur={s.price}
          interval={s.interval || "jednorazový"}
          label={s.interval ? `Objednať za €${s.price}/${s.interval}` : `Kúpiť za €${s.price}`}
          testid={`service-card-${s.id}-buy`}
        />
        <div className="flex gap-2">
          <Link
            to={s.detailsHref}
            data-testid={`service-card-${s.id}-info`}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs font-bold text-white/80 transition"
          >
            Viac info <ArrowRight className="w-3 h-3" />
          </Link>
          <Link
            to="/snapshot"
            data-testid={`service-card-${s.id}-snapshot`}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded text-xs font-bold text-white/80 transition"
          >
            Skúsiť za €7 <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, kicker, items }) {
  return (
    <section className="max-w-6xl mx-auto px-5 sm:px-6 py-10 sm:py-14" data-testid={`services-section-${kicker}`}>
      <div className="mb-8">
        <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#c084fc] font-['JetBrains_Mono'] mb-2">
          {kicker}
        </p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black font-['Cabinet_Grotesk'] text-white tracking-tight">
          {title}
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
        {items.map((s) => <ServiceCard key={s.id} s={s} />)}
      </div>
    </section>
  );
}

export default function ServicesHubPage() {
  const canonical = typeof window !== "undefined" ? `${window.location.origin}/sluzby` : "https://toprankpulse.com/sluzby";
  return (
    <main data-testid="services-hub-page" className="min-h-screen bg-[#0a0a0f] text-white">
      <Helmet>
        <title>Služby TopRankPulse — Kompletný katalóg | Mesačné predplatné aj jednorazové</title>
        <meta
          name="description"
          content="Všetky služby TopRankPulse na jednom mieste. AI-SEO monitoring, Attribution, Autopilot, Blog Packs, Lifetime Deal. Mesačné predplatné zrušiteľné kedykoľvek. GDPR + Stripe secure."
        />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content="Služby TopRankPulse — Kompletný katalóg" />
        <meta property="og:description" content="Všetky služby na jednom mieste. Pro €49/mes, AI Attribution €39/mes, Lifetime €297. Mesačné predplatné zrušiteľné kedykoľvek." />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="sk_SK" />
      </Helmet>

      {/* HERO */}
      <section className="relative px-5 sm:px-6 pt-16 sm:pt-24 pb-10 max-w-5xl mx-auto">
        <div className="absolute inset-x-0 -top-20 h-60 bg-[#a855f7]/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative z-10">
          <p className="text-xs sm:text-sm font-bold uppercase tracking-widest text-[#c084fc] font-['JetBrains_Mono'] mb-3">
            Kompletný katalóg · 2026
          </p>
          <h1
            data-testid="services-hub-h1"
            className="text-4xl sm:text-5xl lg:text-6xl font-black font-['Cabinet_Grotesk'] leading-tight mb-5 tracking-tight"
          >
            Všetky služby <span className="text-[#a855f7]">na jednom mieste</span>
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl leading-relaxed mb-6">
            Od <strong className="text-white">€7 jednorazového Snapshotu</strong> cez <strong className="text-white">€49/mes Pro</strong> plán až po <strong className="text-white">€297 Lifetime Deal</strong>.
            Všetky mesačné predplatné sú <strong className="text-white">zrušiteľné kedykoľvek</strong> v Stripe Customer Portal.
            Žiadne skryté poplatky. GDPR compliant.
          </p>
          <div className="flex flex-wrap gap-3 mb-2">
            <Link
              to="/snapshot"
              data-testid="services-hub-primary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#a855f7] hover:bg-[#9333ea] text-white rounded-lg font-black uppercase tracking-wider text-sm shadow-lg shadow-[#a855f7]/30 transition"
            >
              Začať so €7 Snapshotom <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/pricing"
              data-testid="services-hub-secondary-cta"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/15 text-white rounded-lg font-bold text-sm transition"
            >
              Vidieť plný cenník
            </Link>
          </div>
          <TrustBarSmall testid="services-hub-hero-trust" />
        </div>
      </section>

      <Section title="Hlavné plány" kicker="Flagship Subscriptions" items={SERVICES.flagship} />
      <Section title="Add-ons a Rozšírenia" kicker="Monthly Add-ons" items={SERVICES.addons} />
      <Section title="SEO Command Center" kicker="Organic + Local Suite" items={SERVICES.seo_command} />
      <Section title="Jednorazové produkty" kicker="One-time Deals" items={SERVICES.onetime} />

      {/* Big final CTA — defaults to Pro plan */}
      <div className="max-w-5xl mx-auto px-5 sm:px-6 pb-16">
        <OrderNowBottomCTA
          packageId="pro_monthly"
          priceEur={49}
          interval="mes"
          serviceName="TopRankPulse Pro"
          headline="Nerozhodol si sa? Vyskúšaj Pro"
          subcopy="Neobmedzené AI audity, denné monitorovanie, programmatic SEO. Zruš kedykoľvek jedným klikom."
          testid="services-hub-final-cta"
        />
      </div>
    </main>
  );
}
